import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CreditCard, LogOut, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/store/AppContext';
import { DIRECTUS_INSTANCE } from '@/config/env';
import { getSsoProfileUrl } from '@/config/hosts';
import { useTranslation } from '@/i18n/useTranslation';

const buildSubscriptionSsoUrl = () => getSsoProfileUrl(window.location.href);

function getInitials(first?: string, last?: string, email?: string) {
  const a = (first || '').trim()[0];
  const b = (last || '').trim()[0];
  if (a || b) return `${a || ''}${b || ''}`.toUpperCase();
  return (email || '?').trim()[0]?.toUpperCase() || '?';
}

export default function AccountMenu() {
  const navigate = useNavigate();
  const { user, signOut } = useApp();
  const { t, language } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const displayName = useMemo(() => {
    const firstName = (user?.first_name || '').trim();
    if (firstName) return firstName;
    return user?.email?.split('@')[0] || t('account.defaultName');
  }, [user?.first_name, user?.email, t]);

  const workspaceName = useMemo(() => {
    return user?.linked_workspace?.workspace_name || t('account.defaultWorkspace');
  }, [user?.linked_workspace?.workspace_name, t]);

  const credits = useMemo(() => {
    const value = Number(user?.linked_workspace?.credits);
    if (!Number.isFinite(value) || value < 0) return 0;
    return Math.floor(value);
  }, [user?.linked_workspace?.credits]);

  const formattedCredits = useMemo(
    () => credits.toLocaleString(language === 'pl' ? 'pl-PL' : 'en-US'),
    [credits, language]
  );

  const avatarUrl = useMemo(() => {
    if (!user?.avatar) return null;
    return `${DIRECTUS_INSTANCE}/assets/${user.avatar}`;
  }, [user?.avatar]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full rounded-xl bg-bg-control border border-border hover:bg-bg-control-hover transition-all flex flex-col items-center gap-1.5 py-2 px-1"
        title={`${displayName} - ${workspaceName}`}
      >
        <div className="w-6 h-6 rounded-full bg-accent text-bg-main flex items-center justify-center overflow-hidden text-[11px] font-bold">
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
          ) : (
            <span>{getInitials(user?.first_name, user?.last_name, user?.email)}</span>
          )}
        </div>
        <div className="flex flex-col items-center">
          <span className="text-xs text-text-primary max-w-[62px] truncate leading-tight">{displayName}</span>
          <span className="text-[10px] text-text-secondary max-w-[62px] truncate leading-tight">{workspaceName}</span>
          <span className="text-[11px] font-semibold text-text-primary leading-tight">
            {t('account.credits', { count: formattedCredits })}
          </span>
        </div>
      </button>

      {open && (
        <div className="absolute left-full bottom-0 ml-2 w-48 bg-bg-panel border border-border rounded-xl shadow-2xl overflow-hidden z-[200]">
          <button
            onClick={() => {
              setOpen(false);
              window.location.href = buildSubscriptionSsoUrl();
            }}
            className="w-full px-3 py-2.5 flex items-center gap-2 text-sm text-text-primary hover:bg-bg-control transition-colors"
          >
            <CreditCard size={16} />
            {t('account.billing')}
          </button>
          <button
            onClick={() => {
              setOpen(false);
              navigate('/settings');
            }}
            className="w-full px-3 py-2.5 flex items-center gap-2 text-sm text-text-primary hover:bg-bg-control transition-colors"
          >
            <Settings size={16} />
            {t('account.settings')}
          </button>
          <button
            onClick={() => {
              setOpen(false);
              signOut();
              navigate('/signin');
            }}
            className="w-full px-3 py-2.5 flex items-center gap-2 text-sm text-text-primary hover:bg-bg-control transition-colors"
          >
            <LogOut size={16} />
            {t('account.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
